/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AelNavigationParams,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
  UsageDialogConfig,
  usageDialogRoute,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  BuilderPermissionSet,
  ScheduleTemplateStatus,
  ScheduleType,
  UsageEntityType,
} from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  rendererIconByName,
  ROW_DELETED_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  AppRouter,
  getRouteInitData,
  getRouteStaticData,
  HelpID,
  LocalizationBusinessService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { controlTracker, newSelectionModel, RouteDataService, SelectionModel } from '@gh/core-data';
import { DialogService, newFormControl, NotificationService, WaitingService } from '@gh/core-ui';
import { GridDefinitionFactory, LayoutBasedGridDefinition } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';

import { eq, filter, get } from 'lodash';

import {
  SchedulingScheduleTemplateBusinessService,
} from '../../../resources/scheduling-schedule-template-business.service';
import {
  ScheduleTemplateAelEntityType,
  ScheduleTemplateCheckUsage,
  ScheduleTemplateCreateType,
  ScheduleTemplateDialogConfig,
  ScheduleTemplateListItem,
} from '../../../resources/scheduling-schedule-template.model';
import {
  createScheduleTemplateDialog,
} from '../../scheduling-schedule-template-details-dialog/scheduling-schedule-template-details-dialog.route';

@Component({
  standalone: false,
  selector: 'gh-scheduling-schedule-template-list-subtab',
  templateUrl: 'scheduling-schedule-template-list-subtab.component.html',
})
export class SchedulingScheduleTemplateListSubtabComponent implements OnInit {

  scheduleTemplates: ScheduleTemplateListItem[];
  filteredGridScheduleTemplates: ScheduleTemplateListItem[];
  gridDefinition: LayoutBasedGridDefinition;
  scheduleTemplatesType: ScheduleType;

  showDeleted = newFormControl(false);
  isForWorksheet: SelectionModel;

  get helpId(): HelpID {
    return {
      [ScheduleType.Construction]: 'setup/schedules/constructions/list',
      [ScheduleType.Sales]: 'setup/schedules/sales/list',
      [ScheduleType.PreConstruction]: 'setup/schedules/pre-constructions/list',
      [ScheduleType.Maintenance]: 'setup/schedules/warranty/list',
    }[this.scheduleTemplatesType];
  }

  get isPreConstruction(): boolean {
    return this.scheduleTemplatesType === ScheduleType.PreConstruction;
  }

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private router: AppRouter,
              private gridDefinitionFactory: GridDefinitionFactory,
              private business: SchedulingScheduleTemplateBusinessService,
              private waitingService: WaitingService,
              private notifications: NotificationService,
              private localizationBusinessService: LocalizationBusinessService,
              private dialogService: DialogService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private userContext: BuilderUserContext,
              private navigationService: BuilderNavigationService) {
    this.scheduleTemplatesType = getRouteStaticData(route)['type'];
  }

  ngOnInit(): void {
    const { gridLayout, scheduleTemplates } = getRouteInitData(this.route);

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      duration: {
        valueFormatter: cellValueFormatter<number>((duration) =>
          this.localizationBusinessService.formatHoursDuration(duration)),
      },
      isForWorksheet: {
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
      },
    },                                                                {
      getRowId: (params) => String(params.data.id),
      rowClassRules: {
        [ROW_DELETED_CLASS]: ({ data }: { data: ScheduleTemplateListItem }) =>
          data.active === ScheduleTemplateStatus.Deleted,
      },
    });
    this.gridDefinition.bind(gridLayout);

    this.scheduleTemplates = scheduleTemplates;

    const tracker = this.routeData.tracker(controlTracker(this.showDeleted));

    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.postProcessScheduleTemplates());
  }

  isActive(scheduleTemplate: ScheduleTemplateListItem): boolean {
    return eq(scheduleTemplate.active, ScheduleTemplateStatus.Active);
  }

  onAssignForWorksheet(template: ScheduleTemplateListItem): void {
    this.business.setDefaultForWorksheet(template).pipe(
      (this.waitingService.operator()))
      .subscribe(
        () => this.loadData(),
        this.notifications.processAll,
      );
  }

  onViewUsage({ id: entityId }: ScheduleTemplateListItem): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId,
      entityType: UsageEntityType.SCH_TEMPLATE_USAGE_INFO,
    });
  }

  onEdit({ id }: ScheduleTemplateListItem): void {
    if (this.hasScheduleTemplatePermissionSet('EDIT_VIEW')) {
      this.navigateToEdit(id);
    }
  }

  goToGanttChart({ id }: ScheduleTemplateListItem): void {
    this.router.navigateToChild(['/setup/scheduling/schedule-templates', {
      [ScheduleType.Construction]: 'construction',
      [ScheduleType.Sales]: 'sales',
      [ScheduleType.PreConstruction]: 'pre-construction',
      [ScheduleType.Maintenance]: 'maintenance',
    }[this.scheduleTemplatesType], id, 'gantt-chart']);
  }

  onCreate(): void {
    this.dialogService.openWithInput(createScheduleTemplateDialog, <ScheduleTemplateDialogConfig>{
      type: this.scheduleTemplatesType,
      helpId: {
        [ScheduleType.Construction]: 'setup/schedules/constructions/details',
        [ScheduleType.Sales]: 'setup/schedules/sales/details',
        [ScheduleType.PreConstruction]: 'setup/schedules/pre-constructions/details',
        [ScheduleType.Maintenance]: 'setup/schedules/warranty/details',
      }[this.scheduleTemplatesType],
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe(this.navigateToCreate.bind(this));
  }

  /**
   * Navigate to Create Schedule Template Page.
   * Switch for possible different routing by Schedule Template Type
   * @param {ScheduleTemplateCreateType} createType
   * @param {ScheduleTemplateListItem} scheduleTemplate
   */
  navigateToCreate({ createType, scheduleTemplate }: any): void {
    const queryParams = {
      createType,
      cloneScheduleTemplate: createType === ScheduleTemplateCreateType.Clone
        ? scheduleTemplate.id
        : void 0,
    };

    switch (this.scheduleTemplatesType) {
      case ScheduleType.Construction:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/construction/new/gantt-chart`], { queryParams });
        break;
      case ScheduleType.Sales:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/sales/new/gantt-chart`], { queryParams });
        break;
      case ScheduleType.PreConstruction:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/pre-construction/new/gantt-chart`], { queryParams });
        break;
      case ScheduleType.Maintenance:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/maintenance/new/gantt-chart`], { queryParams });
        break;
      default:
        break;
    }
  }

  /**
   * Navigate to Edit Schedule Template Page.
   * Switch for possible different routing by Schedule Template Type
   */
  navigateToEdit(id: ID): void {
    switch (this.scheduleTemplatesType) {
      case ScheduleType.Construction:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/construction/${id}`]);
        break;
      case ScheduleType.Sales:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/sales/${id}`]);
        break;
      case ScheduleType.PreConstruction:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/pre-construction/${id}`]);
        break;
      case ScheduleType.Maintenance:
        this.router.navigateToChild([`/setup/scheduling/schedule-templates/maintenance/${id}`]);
        break;
      default:
        break;
    }
  }

  onDeactivate(scheduleTemplate: ScheduleTemplateListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_sch_template_mark_deleted',
          params: {},
        },
        confirmNotUsed: {
          id: 'msg_sch_template_delete',
          params: {},
        },
      },
      executes: {
        checkUsage: this.commonActions.execute(({ value }: CommonFlowState<any>) =>
          this.business.checkUsage(value)),
        summary: this.commonActions.execute(({ value }: CommonFlowState<any>) =>
          this.business.deactivateOrDelete(scheduleTemplate, value)),
      },
      finish: ({ value }: { value: ScheduleTemplateCheckUsage }) => {
        if (value && !value.deleted) {
          this.showDeleted.setValue(true);
        }
        this.loadData();
      },
    });
// @ts-expect-error Legacy typing suppression
    flow.run({ value: scheduleTemplate });
  }

  onActivate(scheduleTemplate: ScheduleTemplateListItem): void {
    const flow = this.pipelines.toggleItem({
      question: {
        id: 'msg_sch_template_undelete',
        params: {},
      },
      execute: this.commonActions.execute(({ value }: CommonFlowState<ScheduleTemplateListItem>) =>
        this.business.activate(value)),
      finish: () => this.loadData(),
    });

    flow.run({ value: scheduleTemplate });
  }

  goToAel(entityId?: ID): void {
    this.navigationService.goToAel(
      this.aelNavigationParams(this.scheduleTemplatesType, entityId));
  }

  filterScheduleTemplates(scheduleTemplates: ScheduleTemplateListItem[]): ScheduleTemplateListItem[] {
    return this.showDeleted.value
      ? scheduleTemplates
      : filter(scheduleTemplates, this.isActive);
  }

  hasScheduleTemplatePermissionSet(permission: string): boolean {
    switch (this.scheduleTemplatesType) {
      case ScheduleType.Construction:
        return this.userContext.hasPermission(
          BuilderPermissionSet.Setup.Schedules.ConstructionTemplates[permission]);
      case ScheduleType.Sales:
        return this.userContext.hasPermission(
          BuilderPermissionSet.Setup.Schedules.SalesTemplates[permission]);
      case ScheduleType.PreConstruction:
        return this.userContext.hasPermission(
          BuilderPermissionSet.Setup.Schedules.PreConstructionTemplates[permission]);
      case ScheduleType.Maintenance:
        return this.userContext.hasPermission(
          BuilderPermissionSet.Setup.Schedules.MaintenanceTemplates[permission]);
      default:
        return false;
    }
  }

  hasScheduleTemplatePermissions(permission: string): boolean {
    switch (this.scheduleTemplatesType) {
      case ScheduleType.Construction:
        return this.userContext.hasPermission(
          BuilderPermission.Setup.Schedules.ConstructionTemplates[permission]);
      case ScheduleType.Sales:
        return this.userContext.hasPermission(
          BuilderPermission.Setup.Schedules.SalesTemplates[permission]);
      case ScheduleType.PreConstruction:
        return this.userContext.hasPermission(
          BuilderPermission.Setup.Schedules.PreConstructionTemplates[permission]);
      case ScheduleType.Maintenance:
        return this.userContext.hasPermission(
          BuilderPermission.Setup.Schedules.MaintenanceTemplates[permission]);
      default:
        return false;
    }
  }

  private loadData(): void {
    const gridScheduleTemplates$ = this.business.findAll({
      type: this.scheduleTemplatesType,
    });
    gridScheduleTemplates$.subscribe((gridScheduleTemplates: ScheduleTemplateListItem[]) => {
      this.scheduleTemplates = gridScheduleTemplates;
      this.postProcessScheduleTemplates();
    });
  }

  private aelNavigationParams(type: ScheduleType, id?: ID): AelNavigationParams {
    const entityId = id ? id : undefined;
    const params = {
      [ScheduleType.Construction]: {
        entityId,
        entityType: ScheduleTemplateAelEntityType.Construction,
        label: 'lbl_construction_template',
      },
      [ScheduleType.Sales]: {
        entityId,
        entityType: ScheduleTemplateAelEntityType.Sales,
        label: 'lbl_sales_template',
      },
      [ScheduleType.PreConstruction]: {
        entityId,
        entityType: ScheduleTemplateAelEntityType.PreConstruction,
        label: 'lbl_pre_construction_template',
      },
      [ScheduleType.Maintenance]: {
        entityId,
        entityType: ScheduleTemplateAelEntityType.Maintenance,
        label: 'lbl_maintenance_template',
      },
    };
    return get(params, type);
  }

  private postProcessScheduleTemplates(): void {
    this.filteredGridScheduleTemplates = this.filterScheduleTemplates(this.scheduleTemplates);
    // find pre-construction template selected for worksheet
    this.isForWorksheet = newSelectionModel(
      false,
      this.filteredGridScheduleTemplates
        .filter((template) => template.isForWorksheet)
        .map((template) => template.id));
  }
}
